/**
 * Generic SSE Hook
 *
 * Provides a reusable hook for consuming Server-Sent Events streams.
 * Handles connection management, progress tracking, and cleanup.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { SSEProgressEvent } from '@bstock/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface UseSSEOptions<T> {
  /** Callback fired on each progress event */
  onProgress?: (event: SSEProgressEvent) => void;
  /** Callback fired when operation completes successfully */
  onDone?: (result: T) => void;
  /** Callback fired on error */
  onError?: (error: string) => void;
}

interface UseSSEResult<T> {
  /** Start the SSE connection (optional query params appended to the URL). */
  start: (queryParams?: Record<string, string | undefined>) => void;
  /** Stop/abort the SSE connection */
  stop: () => void;
  /** Current progress event (null if not started or after completion) */
  progress: SSEProgressEvent | null;
  /** Final result (null until completion) */
  result: T | null;
  /** Error message (null if no error) */
  error: string | null;
  /** Whether the SSE connection is active */
  isLoading: boolean;
}

/**
 * Generic hook for consuming SSE streams
 *
 * @param path - API path (relative to API base URL, e.g., '/sales/sync/stream')
 * @param options - Callbacks for progress, completion, and error handling
 * @returns SSE state and control functions
 *
 * @example
 * ```tsx
 * const { start, stop, progress, isLoading, error } = useSSE<SyncResult>(
 *   '/sales/sync/stream',
 *   {
 *     onDone: (result) => console.log('Done!', result),
 *     onError: (error) => console.error('Failed:', error),
 *   }
 * );
 * ```
 */
export function useSSE<T = unknown>(
  path: string,
  options?: UseSSEOptions<T>
): UseSSEResult<T> {
  const [progress, setProgress] = useState<SSEProgressEvent | null>(null);
  const [result, setResult] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const optionsRef = useRef(options);
  // Track if we received a successful result (to ignore close errors after success)
  const receivedResultRef = useRef(false);
  // Track if we received an applicative error from server (to avoid overwriting
  // it with "Connexion perdue" when EventSource fires its native onerror after
  // the server closes the stream).
  const receivedAppErrorRef = useRef(false);

  // Keep options ref updated
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const stop = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsLoading(false);
    }
  }, []);

  const start = useCallback((queryParams?: Record<string, string | undefined>) => {
    // Reset state
    setProgress(null);
    setResult(null);
    setError(null);
    setIsLoading(true);
    receivedResultRef.current = false;
    receivedAppErrorRef.current = false;

    // Get auth token from localStorage
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setError('Non authentifie');
      setIsLoading(false);
      return;
    }

    // Build URL with auth token as query param (EventSource doesn't support headers)
    const baseUrl = API_BASE_URL.startsWith('http')
      ? API_BASE_URL
      : `${window.location.origin}${API_BASE_URL}`;
    const params = new URLSearchParams();
    params.set('token', token);
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, value);
        }
      }
    }
    const url = `${baseUrl}${path}${path.includes('?') ? '&' : '?'}${params.toString()}`;

    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    // Progress events
    eventSource.addEventListener('progress', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as SSEProgressEvent;
        setProgress(data);
        optionsRef.current?.onProgress?.(data);
      } catch (err) {
        console.error('[useSSE] Failed to parse progress event:', err);
      }
    });

    // Result event (operation completed successfully)
    eventSource.addEventListener('result', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as T;
        receivedResultRef.current = true; // Mark success before close
        setResult(data);
        optionsRef.current?.onDone?.(data);
      } catch (err) {
        console.error('[useSSE] Failed to parse result event:', err);
      }
    });

    // Error event from server
    eventSource.addEventListener('error', (e: Event) => {
      // Check if it's a MessageEvent with data (server-sent error)
      if (e instanceof MessageEvent && e.data) {
        try {
          const data = JSON.parse(e.data);
          const errorMessage = data.message || 'Erreur inconnue';
          receivedAppErrorRef.current = true;
          setError(errorMessage);
          optionsRef.current?.onError?.(errorMessage);
        } catch {
          // Not a JSON error, might be connection error
        }
      }
    });

    // Done event (stream complete)
    eventSource.addEventListener('done', () => {
      stop();
    });

    // Connection error (network issue, server down, etc.)
    eventSource.onerror = (e) => {
      // Only treat as error if we haven't received a successful result
      if (eventSourceRef.current) {
        // If we already received the result, just close cleanly
        if (receivedResultRef.current) {
          stop();
          return;
        }

        // If we already received an applicative error from server, just close
        // — don't overwrite the error message with "Connexion perdue".
        if (receivedAppErrorRef.current) {
          stop();
          return;
        }

        console.error('[useSSE] Connection error:', e);
        // Check if connection was closed by server (readyState === 2)
        if (eventSource.readyState === EventSource.CLOSED) {
          // Connection closed normally after done event
          stop();
        } else {
          // Actual error
          const errorMessage = 'Connexion perdue';
          setError(errorMessage);
          optionsRef.current?.onError?.(errorMessage);
          stop();
        }
      }
    };
  }, [path, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  return { start, stop, progress, result, error, isLoading };
}
